import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

import { buttonClass, type ButtonVariant, type ButtonSize } from './button-class.ts';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly 'data-testid'?: string;
};

/**
 * Forwards its ref: a Radix `asChild` trigger (Tooltip, Popover) clones its child
 * and needs the real DOM node to anchor and position content. React 18 DROPS a ref
 * given to a plain function component, silently, so the tooltip would simply lose
 * its anchor. Keep the forwardRef when editing this. Same for ButtonLink.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, className, type = 'button', ...props },
  ref,
) {
  return <button ref={ref} type={type} className={buttonClass(variant, size, className)} {...props} />;
});

export type ButtonLinkProps = LinkProps & {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly 'data-testid'?: string;
};

export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(
  { variant, size, className, ...props },
  ref,
) {
  return <Link ref={ref} className={buttonClass(variant, size, className)} {...props} />;
});
